<template>
  <CollapsibleSection
    ref="collapsibleSectionRef"
    title="Label Sections and Style"
    :default-open="true"
    section-key="label-settings"
    test-id="label-settings-section"
    :no-panel-block="true"
  >
    <CheckboxWithHint
      v-model="store.hideFacts"
      label="Hide supplement facts"
      hint="This hides the nutrition facts table from the label for printing. This can be useful if you only need to print ingredients or allergens."
      test-id="hide-facts-checkbox"
    />

    <CheckboxWithHint
      v-model="store.hideOtherIngredients"
      label="Hide other ingredients"
      hint="We provide the option to print/save the nutrition label along with the ingredient list, but it's perfectly OK to do them separately. Ingredient lists are <a href='/blogs/27-my-food-product-packaging-label-requirements' target='_blank'>required on your supplement packaging</a>. You can edit your ingredient list from the Recipe Actions menu above."
      :interactive="true"
      test-id="hide-other-ingredients-checkbox"
      class="has-bold-options"
    >
      <template #actions>
        <BoldOptionsToggle v-model="expandedBoldOptions.otherIngredients" />
      </template>
    </CheckboxWithHint>

    <BoldOptionsGroup
      :expanded="expandedBoldOptions.otherIngredients"
      :label-text="otherIngredientsLabelPrefix"
      :label-bold="boldOtherIngredientsLabel"
      :text-bold="boldAllOtherIngredients"
      text-bold-label="Bold all other ingredients"
      test-id-prefix="other-ingredients"
      text-test-id-prefix="all-other-ingredients"
      :hint-content="HINT_TEXTS.SMART_BOLDING_WITH_FORMATTING"
      @update:label-bold="boldOtherIngredientsLabel = $event"
      @update:text-bold="boldAllOtherIngredients = $event"
    />

    <CheckboxWithHint
      v-model="store.hideAllergens"
      label="Hide allergens"
      hint="We provide the option to print/save the nutrition label along with the allergen list, but it's perfectly OK to do them separately. Allergen lists are <a href='/blogs/27-my-food-product-packaging-label-requirements' target='_blank'>required on your supplement packaging</a>."
      :interactive="true"
      test-id="hide-allergens-checkbox"
      class="has-bold-options"
    >
      <template #actions>
        <BoldOptionsToggle v-model="expandedBoldOptions.allergens" />
      </template>
    </CheckboxWithHint>

    <BoldOptionsGroup
      :expanded="expandedBoldOptions.allergens"
      :label-text="allergensLabelPrefix"
      :label-bold="boldAllergensLabel"
      :text-bold="boldAllergens"
      text-bold-label="Bold allergens text"
      test-id-prefix="allergens"
      :hint-content="HINT_TEXTS.CUSTOM_FORMATTING"
      @update:label-bold="boldAllergensLabel = $event"
      @update:text-bold="boldAllergens = $event"
    />

    <CheckboxWithHint
      v-model="store.hideFacilityAllergens"
      label="Hide facility allergens"
      hint="We provide the option to print/save the nutrition label along with an advisory facility allergen list stating what allergens are used in your production facility, but it's OK to leave this section off as it is strictly optional. Since it's optional, by default we don't display it."
      test-id="hide-facility-allergens-checkbox"
      class="has-bold-options"
    >
      <template #actions>
        <BoldOptionsToggle v-model="expandedBoldOptions.facilityAllergens" />
      </template>
    </CheckboxWithHint>

    <BoldOptionsGroup
      :expanded="expandedBoldOptions.facilityAllergens"
      :label-text="facilityAllergensLabelPrefix"
      :label-bold="boldFacilityAllergensLabel"
      :text-bold="boldFacilityAllergens"
      text-bold-label="Bold facility allergens text"
      test-id-prefix="facility-allergens"
      :hint-content="HINT_TEXTS.CUSTOM_FORMATTING"
      @update:label-bold="handleBoldFacilityAllergensLabelChange"
      @update:text-bold="boldFacilityAllergens = $event"
    />

    <CheckboxWithHint
      v-model="store.hideBusinessInfo"
      label="Hide business info"
      hint="We provide the option to print/save the nutrition label along with the business name/address, but it's perfectly OK to do them separately. The business name/address is <a href='/blogs/27-my-food-product-packaging-label-requirements' target='_blank'>required on your supplement packaging</a>."
      :interactive="true"
      test-id="hide-business-info-checkbox"
      class="has-bold-options"
    >
      <template #actions>
        <BoldOptionsToggle v-model="expandedBoldOptions.businessInfo" />
      </template>
    </CheckboxWithHint>

    <BoldOptionsGroup
      :expanded="expandedBoldOptions.businessInfo"
      :show-label-bold="false"
      :text-bold="boldBusinessInfo"
      text-bold-label="Bold business info"
      test-id-prefix="business-info"
      :hint-content="HINT_TEXTS.CUSTOM_FORMATTING"
      @update:text-bold="boldBusinessInfo = $event"
    />

    <CheckboxWithHint
      v-model="store.bioengineered"
      label="Contains bioengineered food"
      hint='Under some circumstances, foods that contain only bioengineered ingredients should be described as a "bioengineered food." If your product meets these requirements, check select this option to use this language in the bioengineered claim.'
      test-id="bioengineered-checkbox"
    />

    <CheckboxWithHint
      v-model="store.useLargerFootnotes"
      label="Use larger size for footnotes"
      hint="When enabled, footnotes at the bottom of the label will use the same font size as the rest of the label. By default, footnotes use a smaller font size (the standard convention)."
      test-id="use-larger-footnotes-checkbox"
    />

    <!-- Logo Section -->
    <CheckboxWithHint
      v-model="store.hideLogo"
      label="Hide logo"
      hint="You can optionally add your company logo above the Nutrition Facts label."
    >
      <template #actions>
        <a
          v-if="!store.hideLogo && !store.isPublic"
          href="#"
          class="tag is-info ml-3"
          @click.prevent="openLogoModal"
        >
          Manage logo
        </a>
      </template>
    </CheckboxWithHint>

    <!-- Logo Scale Slider (only show if logo exists and not hidden) -->
    <SliderWithReset
      v-if="store.logoImage && !store.hideLogo"
      v-model="store.logoScale"
      label="Logo scale"
      :display-value="logoScaleDisplay"
      :min="LOGO_CONSTANTS.MIN_SCALE"
      :max="LOGO_CONSTANTS.MAX_SCALE"
      :step="LOGO_CONSTANTS.SCALE_STEP"
      :default-value="LOGO_CONSTANTS.DEFAULT_SCALE"
      parse-as="float"
      input-id="logo-scale-slider"
      test-id="logo-scale-slider"
      @reset="resetLogoScale"
    />

    <!-- Logo Margin Slider (only show if logo exists and not hidden) -->
    <SliderWithReset
      v-if="store.logoImage && !store.hideLogo"
      v-model="store.logoMargin"
      label="Logo margin"
      :display-value="logoMarginDisplay"
      :min="LOGO_CONSTANTS.MIN_MARGIN"
      :max="LOGO_CONSTANTS.MAX_MARGIN"
      :step="LOGO_CONSTANTS.MARGIN_STEP"
      :default-value="LOGO_CONSTANTS.DEFAULT_MARGIN"
      input-id="logo-margin-slider"
      test-id="logo-margin-slider"
      @reset="resetLogoMargin"
    />

    <!-- Barcode Section -->
    <CheckboxWithHint
      v-model="store.hideBarcode"
      label="Hide barcode"
      hint="Add a barcode to your label. Learn more about barcodes for supplement labels <a href='/blog/getting-started-with-food-labels/barcodes-for-food-labels' target='_blank'>in our blog post.</a>"
      :interactive="true"
      test-id="hide-barcode-checkbox"
    >
      <template #actions>
        <a
          v-if="!store.hideBarcode"
          href="#"
          class="tag is-info ml-3"
          @click.prevent="openBarcodeModal"
        >
          Manage barcode
        </a>
      </template>
    </CheckboxWithHint>

    <!-- Label Style (matches Recipe "Label style" panel order) -->

    <!-- Label Width Slider -->
    <SliderWithReset
      v-model="store.labelWidth"
      label="Label width"
      :display-value="labelWidthDisplay"
      :min="LABEL_WIDTH_CONSTANTS.MIN"
      :max="effectiveMaxWidth"
      :step="LABEL_WIDTH_CONSTANTS.STEP"
      :default-value="LABEL_WIDTH_CONSTANTS.DEFAULT"
      input-id="label-width-slider"
      test-id="label-width-slider"
      @reset="resetLabelWidth"
    >
      <template #hint>
        <IconHint
          content="Certain label specifications are not set in stone by the FDA, so you can change them if you want. For example, you can make the label narrower or wider and change text and background colors. The default is the most common and accepted combination of options, but you can adjust these to make them fit your packaging aesthetic."
        />
      </template>
      <template #footer>
        <div
          v-if="store.renderedDimensionsText"
          class="has-text-grey has-text-centered is-size-7"
          data-test-id="label-dimensions"
        >
          {{ store.renderedDimensionsText }}
        </div>
      </template>
    </SliderWithReset>

    <!-- Text Alignment & Capitalization (side-by-side) -->
    <div class="panel-block is-block pb-3">
      <div class="columns is-mobile is-variable is-2 mb-0">
        <div class="column">
          <label for="text-align-select" class="label has-text-weight-normal mb-1">
            Text alignment
            <IconHint
              content="This is purely aesthetics. A lot of packaging justifies text to both sides in the ingredient list, allergens, and business address, but you don't have to, so feel free to justify it left or any of the other options."
            />
          </label>
          <div class="control">
            <div class="select is-fullwidth">
              <select
                id="text-align-select"
                v-model="store.textAlign"
                data-test-id="text-align-select"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="justify">Justify</option>
              </select>
            </div>
          </div>
        </div>
        <div class="column">
          <label for="text-transform-select" class="label has-text-weight-normal mb-1">
            Text capitalization
            <IconHint
              content="This is purely aesthetics. A lot of packaging uses uppercase in the ingredient list, allergens, and business address, but you don't have to, so feel free to remove it or use any of the other capitalization options. Removing uppercase will display your ingredient list as it was entered on the Ingredient List page."
            />
          </label>
          <div class="control">
            <div class="select is-fullwidth">
              <select
                id="text-transform-select"
                v-model="store.textTransform"
                data-test-id="text-transform-select"
              >
                <option value="none">None (as entered)</option>
                <option value="uppercase">UPPERCASE</option>
                <option value="lowercase">lowercase</option>
                <option value="capitalize">Title Case</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Colors (side-by-side) -->
    <div class="panel-block is-block" style="border-bottom: none">
      <div class="columns is-mobile is-variable is-2 mb-0">
        <div class="column">
          <ColorInput
            v-model="store.textColor"
            label="Text color"
            hint="Label font and outline color is normally black, but it can be changed to fit your label aesthetic. Outlines and font must remain one color and keep in mind that the goal is to make the label easily identifiable and readable. Enter color as HEX (#RRGGBB), rgb (rgb(X,Y,Z)), or a color name. Google 'CSS colors' to learn more if this is confusing!"
            default-color="black"
            default-color-hex="#000000"
            placeholder="black"
            input-id="text-color-input"
            test-id="text-color-input"
          />
        </div>
        <div class="column">
          <ColorInput
            v-model="store.backgroundColor"
            label="Background color"
            hint="Label background color is normally white, but it can be changed to fit your label aesthetic, as long as it remains a neutral contrasting color. Again, keep in mind that the goal is to make the label easily identifiable and readable. Enter color as HEX (#RRGGBB), rgb (rgb(X,Y,Z)), or a color name. Google 'CSS colors' to learn more if this is confusing!"
            default-color="white"
            default-color-hex="#ffffff"
            placeholder="white"
            input-id="background-color-input"
            test-id="background-color-input"
            :disabled="store.transparentBackground"
            disabled-placeholder="Transparent"
          />
        </div>
      </div>
    </div>

    <!-- Transparent Background Toggle -->
    <CheckboxWithHint
      v-model="store.transparentBackground"
      label="Transparent background (PNG only)"
      hint="PNG exports only: This will apply a transparent background to your exported label. You can use this to overlay the label on custom backgrounds or packaging, though keep in mind that this can impact the reability of your label. The FDA recommends a stark contrast between the background and text on the Nutrition Facts label for readability."
      test-id="transparent-background-checkbox"
    />
  </CollapsibleSection>
</template>

<script setup>
  import { computed, watch, ref } from "vue";
  import { useSupplementLabelStore } from "../stores/supplement-label-store";
  import CollapsibleSection from "./collapsible-section.vue";
  import IconHint from "../icon-hint.vue";
  import SliderWithReset from "./slider-with-reset.vue";
  import CheckboxWithHint from "./checkbox-with-hint.vue";
  import ColorInput from "./color-input.vue";
  import BoldOptionsToggle from "./bold-options-toggle.vue";
  import BoldOptionsGroup from "./bold-options-group.vue";
  import { LOGO_CONSTANTS } from "~/label/logo/constants";
  import { HINT_TEXTS } from "./utils/constants";
  import { BARE_PREFIX_PATTERN, stripMarkdownMarkersForPrefix } from "./utils/formatting";

  const store = useSupplementLabelStore();
  const collapsibleSectionRef = ref(null);

  // Track which bold option groups are expanded
  const expandedBoldOptions = ref({
    otherIngredients: false,
    allergens: false,
    facilityAllergens: false,
    businessInfo: false,
  });

  // Helper to create computed property for a bold option with custom default
  const createBoldOption = (key, defaultValue = false) =>
    computed({
      get: () => store.boldOptions[key] ?? defaultValue,
      set: (value) => {
        store.boldOptions = { ...store.boldOptions, [key]: value };
      },
    });

  // Bold options computed properties
  // Label options default to true (bold), text options default to false (not bold)
  const boldOtherIngredientsLabel = createBoldOption("other_ingredients_label", true);
  const boldAllOtherIngredients = createBoldOption("all_other_ingredients", false);
  const boldAllergensLabel = createBoldOption("allergens_label", true);
  const boldAllergens = createBoldOption("allergens", false);
  const boldFacilityAllergensLabel = createBoldOption("facility_allergens_label", true);
  const boldFacilityAllergens = createBoldOption("facility_allergens", false);
  const boldBusinessInfo = createBoldOption("business_info", false);

  function extractPrefix(text, defaultPrefix) {
    if (!text) return defaultPrefix;
    const stripped = stripMarkdownMarkersForPrefix(text);
    const match = stripped.match(BARE_PREFIX_PATTERN);
    if (match) {
      return match[0].trim();
    }
    return defaultPrefix;
  }

  const otherIngredientsLabelPrefix = computed(() =>
    extractPrefix(store.otherIngredients, "Other ingredients:")
  );

  const allergensLabelPrefix = computed(() => extractPrefix(store.allergens, "Contains:"));

  const facilityAllergensLabelPrefix = computed(() =>
    extractPrefix(store.facilityAllergens, store.defaultFacilityAllergensPrefix)
  );

  function handleBoldFacilityAllergensLabelChange(isChecked) {
    boldFacilityAllergensLabel.value = isChecked;

    if (isChecked) {
      const currentValue = store.facilityAllergens || "";
      const hasPrefix = /^[A-Z][A-Za-z\s/]*:/.test(currentValue);
      if (!hasPrefix) {
        store.facilityAllergens = store.defaultFacilityAllergensPrefix + currentValue;
      }
    }
  }

  // Clear background color when transparent background is enabled
  watch(
    () => store.transparentBackground,
    (isTransparent) => {
      if (isTransparent && store.backgroundColor) {
        store.backgroundColor = "";
      }
    }
  );

  const LABEL_WIDTH_CONSTANTS = {
    MIN: 180,
    MAX: 350,
    DEFAULT: 227,
    STEP: 1,
  };

  // Dynamic max width that increases with additional age category columns
  const effectiveMaxWidth = computed(() => {
    return LABEL_WIDTH_CONSTANTS.MAX + store.additionalWidthForColumns;
  });

  // Display effective width (base + additional column width)
  const labelWidthDisplay = computed(() => {
    return String(store.effectiveLabelWidth);
  });

  const logoScaleDisplay = computed(() => {
    return (store.logoScale || LOGO_CONSTANTS.DEFAULT_SCALE).toFixed(2) + "x";
  });

  const logoMarginDisplay = computed(() => {
    return (store.logoMargin ?? LOGO_CONSTANTS.DEFAULT_MARGIN) + "px";
  });

  function resetLabelWidth() {
    store.labelWidth = LABEL_WIDTH_CONSTANTS.DEFAULT;
  }

  function resetLogoScale() {
    store.logoScale = LOGO_CONSTANTS.DEFAULT_SCALE;
  }

  function resetLogoMargin() {
    store.logoMargin = LOGO_CONSTANTS.DEFAULT_MARGIN;
  }

  function openLogoModal() {
    window.dispatchEvent(new CustomEvent("supplement:open-logo-modal"));
  }

  function openBarcodeModal() {
    window.dispatchEvent(new CustomEvent("supplement:open-barcode-modal"));
  }
</script>

<style lang="scss">
  @import "~jose_modern/css";

  // Non-scoped styles for bold options toggle behavior
  // Make the inner flex container expand to allow right alignment
  .has-bold-options .is-flex.is-align-items-center {
    flex-grow: 1;
  }

  // Always show ellipsis, aligned to right with spacing from hint icon
  .has-bold-options .bold-options-toggle {
    margin-left: auto;
    margin-right: 0.5rem;
  }

  .bold-options-group > .panel-block {
    border-bottom: 1px solid #ededed;
    border-radius: 0;
  }
</style>

<style lang="scss" scoped>
  @import "~jose_modern/css";

  // Slide transition for bold options groups
  .slide-enter-active,
  .slide-leave-active {
    transition: all 0.3s ease;
    max-height: 500px;
    overflow: hidden;
  }

  .slide-enter-from,
  .slide-leave-to {
    max-height: 0;
    opacity: 0;
  }

  .slide-enter-to,
  .slide-leave-from {
    max-height: 500px;
    opacity: 1;
  }
</style>
